import React, { useRef, useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Flex
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookAlt,
  faFileAlt,
  faFileEdit,
  faFileMagnifyingGlass,
  faUser,
  faUsers
} from '@fortawesome/pro-regular-svg-icons';
import Divider from 'src/components/Divider';
import { useRevisionStepsConfigs } from 'src/hooks/useRevisionStepsConfigs';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { ScrollContext } from 'src/components/RevisionOverlay';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';

interface Props {
  currentStep: number;
  onNavigate: (step: number) => void;
}

type Section = {
  id: string;
  label: string;
  step: number;
};

export const titleMap: Array<string> = [
  'Revision upload & feedback response',
  'Submission information',
  'Author information',
  'Submission preferences',
  'Review revised submission',
  'Review submission'
];

export const iconMap: Record<string, IconDefinition> = {
  'Revision upload & feedback response': faBookAlt,
  'Submission information': faFileAlt,
  'Author information': faUsers,
  'Submission preferences': faFileEdit,
  'Review revised submission': faFileMagnifyingGlass,
  'Review submission': faFileMagnifyingGlass
};

export const RevisionSidebar: React.FC<Props> = ({
  currentStep,
  onNavigate
}) => {
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const { steps } = useRevisionStepsConfigs();
  const [isActiveText, setIsActiveText] = useState<string>('');
  const scrollRef = React.useContext(ScrollContext);
  const groups = steps
    .map((stepItem, originalIndex) => {
      const componentName = stepItem.displayName as string;

      // Only keep mapped steps
      if (!titleMap.includes(componentName)) return null;

      return { stepItem, originalIndex };
    })
    .filter(
      (g): g is { stepItem: (typeof steps)[number]; originalIndex: number } =>
        g !== null
    )
    .map((g, index) => {
      const { stepItem, originalIndex } = g;

      const title = stepItem.displayName as string;
      const iconName = iconMap[title];

      // Step number is based on filtered index
      const stepNumber = index + 1;

      const sections: Section[] = (stepItem.sections ?? []).map(
        (section): Section => ({
          id: section.toLowerCase().replace(/\s+/g, '-'),
          label: section,
          step: stepNumber
        })
      );

      return {
        id: title.toLowerCase().replace(/\s+/g, '-'),
        title,
        step: stepNumber,
        originalIndex,
        sections,
        iconName
      };
    });

  const scrollToTop = () =>
    scrollRef?.current && scrollRef.current.scrollTo({ top: 0 });

  const { checkFormFieldValueIsUpdated } = useSubmissionFields('submission');
  const {
    activeRevision,
    workflowResponse: {
      workflowData: { currentState }
    }
  } = useSubmissionData();

  const isTitlePageValueUpadted = checkFormFieldValueIsUpdated('TitlePage');
  const isManuscriptValueUpadted = checkFormFieldValueIsUpdated('Manuscript');

  const hasUpdatedForm =
    currentState === 'unsubmitted' ||
    (isTitlePageValueUpadted &&
      isManuscriptValueUpadted &&
      !!activeRevision?.authorResponse);

  return (
    <>
      {/* Mobile: horizontal scrollable step indicator */}
      <Box display={{ base: 'block', md: 'none' }}>
        <Box overflowX="auto" pb={1}>
          <Flex gap={0} minWidth="max-content" alignItems="stretch">
            {groups.map((group, index) => (
              <React.Fragment key={group.id}>
                <Flex
                  direction="column"
                  alignItems="center"
                  gap={1}
                  px={3}
                  py={2}
                  cursor={
                    !hasUpdatedForm && group.step !== currentStep + 1
                      ? 'not-allowed'
                      : 'pointer'
                  }
                  opacity={
                    !hasUpdatedForm && group.step !== currentStep + 1 ? 0.5 : 1
                  }
                  borderBottom="3px solid"
                  borderColor={
                    group.step === currentStep + 1
                      ? 'actionSecondaryContent'
                      : 'transparent'
                  }
                  color={
                    group.step === currentStep + 1
                      ? 'actionSecondaryContent'
                      : undefined
                  }
                  onClick={() => {
                    if (hasUpdatedForm || group.step === currentStep + 1) {
                      onNavigate(group.originalIndex);
                      scrollToTop();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={group.iconName ?? faUser} />
                  <Text
                    textStyle="textContext"
                    fontSize="xs"
                    whiteSpace="nowrap"
                    fontWeight={
                      group.step === currentStep + 1 ? 'bold' : undefined
                    }
                  >
                    {group.title}
                  </Text>
                </Flex>
                {index < groups.length - 1 && (
                  <Flex alignItems="center">
                    <Box w="20px" h="1px" bgColor="neutralPrimaryBorder" />
                  </Flex>
                )}
              </React.Fragment>
            ))}
          </Flex>
        </Box>
      </Box>

      {/* Desktop: accordion sidebar (unchanged) */}
      <VStack
        align="stretch"
        spacing={2}
        display={{ base: 'none', md: 'flex' }}
      >
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingItem">
            {currentState === 'unsubmitted' ? 'Submit ' : 'Revise your '}
            submission
          </Text>
          {Divider}
        </Flex>
        <Accordion allowToggle defaultIndex={[currentStep]}>
          {groups.map(group => (
            <AccordionItem
              key={group.id}
              border="none"
              isDisabled={!hasUpdatedForm && group.step !== currentStep + 1}
            >
              {() => (
                <>
                  <AccordionButton
                    borderLeft={
                      group.step === currentStep + 1 ? '3px solid' : '3px solid'
                    }
                    borderColor={
                      group.step === currentStep + 1
                        ? 'actionSecondaryContent'
                        : 'transparent'
                    }
                    onClick={() => {
                      onNavigate(group.originalIndex);
                      scrollToTop();
                    }}
                    _disabled={{
                      opacity: 1,
                      cursor: 'not-allowed'
                    }}
                    justifyContent="flex-start"
                  >
                    <Flex
                      justify="flex-start"
                      alignItems="baseline"
                      color={
                        group.step === currentStep + 1
                          ? 'actionSecondaryContent'
                          : undefined
                      }
                      gap={2}
                    >
                      <Box>
                        <FontAwesomeIcon icon={group.iconName ?? faUser} />
                      </Box>
                      <Text
                        textStyle="headingItem"
                        whiteSpace="normal"
                        textAlign="left"
                        flex={1}
                      >
                        {group.title}
                      </Text>
                    </Flex>
                  </AccordionButton>

                  <AccordionPanel pe={3}>
                    <Box
                      borderLeft="1px solid"
                      borderColor="neutralPrimaryBorder"
                      pl={3}
                    >
                      <VStack align="stretch" spacing={1}>
                        {group.sections.map(section => {
                          const target = document.getElementById(section.label);

                          return (
                            <Box
                              key={section.id}
                              ref={el => (refs.current[section.id] = el)}
                              px={3}
                              py={1}
                              cursor="pointer"
                              _focus={{
                                boxShadow: 'outline',
                                bg: 'surfaceSecondaryFill'
                              }}
                              _hover={{ bg: 'surfaceSecondaryFill' }}
                              onClick={() => {
                                setIsActiveText(section.label);
                                if (target && scrollRef?.current) {
                                  const container = scrollRef.current;

                                  const targetTop =
                                    target.offsetTop - container.offsetTop - 90;

                                  container.scrollTo({
                                    top: targetTop,
                                    behavior: 'smooth'
                                  });
                                }
                              }}
                            >
                              <Text
                                textStyle={'textContext'}
                                fontWeight={
                                  isActiveText === section.label
                                    ? 'bold'
                                    : undefined
                                }
                              >
                                {section.label}
                              </Text>
                            </Box>
                          );
                        })}
                      </VStack>
                    </Box>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          ))}
        </Accordion>
      </VStack>
    </>
  );
};
